import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { matchPath } from '../utils/reactRouterCompatibility';
import { getMode } from '@karpeleslab/klbfw';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import routes, { layoutLessRoute } from '../../router/routeConfig';

import ScrollToTop from '../Route/ScrollToTop';
import LoginRequired from '../Route/LoginRequired';
import { CssBaseline, Box } from '@mui/material';
import AppBar from './AppBar';
import { AppBarSpacer, MainContainer, MainContent, Root } from './style';
import Toasts from '../common/feeback/Toasts';
import RealmLoader from '../common/realm/Loader';
import { useSelector } from 'react-redux';
import Theme from './Theme';
import ErrorBoundary from './ErrorBoundary';
import Drawer from './drawer/Drawer';
import Menu from './drawer/Menu/Menu';
import { drawerWidth } from './drawer/style';

const ContentWrapper = styled(Box, {
	shouldForwardProp: prop => prop !== 'hasDrawer'
})(({ theme, hasDrawer }) => ({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	marginLeft: hasDrawer ? drawerWidth : 0,
	transition: theme.transitions.create('margin', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	minHeight: '100vh',
	width: hasDrawer ? `calc(100% - ${drawerWidth}px)` : '100%',
}));

const Layout = () => {
	const mode = getMode();
	const location = useLocation();
	const user = useSelector(state => state.user.user);
	const theme = useTheme();
	const mobile = useMediaQuery(theme.breakpoints.down('md'));

	const buildRoute = (idx, route) => {
		if ('mode' in route) {
			if (Array.isArray(route.mode)) {
				if (!route.mode.includes(mode)) return null;
			} else if (mode !== route.mode)
				return null;
		}

		const C = 'components' in route ? route.components : route.component;

		if ('loginRequired' in route && route.loginRequired === true) {
			const routeProps = { ...route };
			delete routeProps.component;
			delete routeProps.components;
			delete routeProps.loginRequired;

			return (
				<Route
					key={idx}
					{...routeProps}
					element={
						<LoginRequired>
							{'components' in route ? C : <C />}
						</LoginRequired>
					}
				/>
			);
		}

		const routeProps = { ...route };
		delete routeProps.component;
		delete routeProps.components;

		return (
			<Route
				key={idx}
				{...routeProps}
				element={'components' in route ? C : <C />}
			/>
		);
	};

	const buildRoutes = () => {
		return (
			<Routes>
				{routes.map((route, idx) => {
					switch (route.type) {
						case 'route':
						default:
							return buildRoute(idx, { ...route });
					}
				})}
			</Routes>
		);
	};

	let hasLayoutLessRoute = false;

	for (let i = 0; i < layoutLessRoute.length; i++) {
		const match = matchPath(layoutLessRoute[i], location.pathname);
		if (match) {
			hasLayoutLessRoute = true;
			break;
		}
	}

	const addLayout = mode !== 'ssr-open' &&
		mode !== 'client-open' &&
		mode !== 'ssr-mail' && !hasLayoutLessRoute;

	if (!addLayout) {
		return (
			<Theme>
				<CssBaseline />
				<ErrorBoundary>
					{buildRoutes()}
				</ErrorBoundary>
			</Theme>
		);
	}

	const hasDrawer = user && !mobile;

	return (
		<Root>
			<Theme>
				<CssBaseline />
				<AppBar />
				<Drawer />
				<Menu />
				<ScrollToTop />
				{user && <RealmLoader />}
				<ContentWrapper hasDrawer={hasDrawer}>
					<AppBarSpacer />
					<MainContent>
						<MainContainer maxWidth="xl">
							<ErrorBoundary>
								{buildRoutes()}
							</ErrorBoundary>
						</MainContainer>
					</MainContent>
				</ContentWrapper>
				<Toasts />
			</Theme>
		</Root>
	);
};

export default Layout;
